import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  BarChart3,
  Brain,
  Briefcase,
  CheckCircle,
  FileText,
  Globe2,
  MessageSquare,
  Settings,
  ShieldCheck,
  Sparkles,
  Star,
  Zap,
} from "lucide-react";
import StickyWorkflowShowcase from "./home/StickyWorkflowShowcase";

function GoogleText() {
  return (
    <span className="font-semibold">
      <span className="text-blue-500">G</span>
      <span className="text-red-500">o</span>
      <span className="text-yellow-500">o</span>
      <span className="text-blue-500">g</span>
      <span className="text-green-500">l</span>
      <span className="text-red-500">e</span>
    </span>
  );
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
}

const sectionHeadingClass =
  "text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl";
const gradientTextClass =
  "bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent";

function HeroProductPreview() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [matchScore, setMatchScore] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      setMatchScore(92);
      return undefined;
    }

    let frameId;
    let startTime;
    const duration = 1800;

    const animateScore = (timestamp) => {
      if (!startTime) startTime = timestamp;

      const progress = Math.min((timestamp - startTime) / duration, 1);
      setMatchScore(Math.round(progress * 92));

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animateScore);
      }
    };

    frameId = window.requestAnimationFrame(animateScore);

    return () => window.cancelAnimationFrame(frameId);
  }, [prefersReducedMotion]);

  const resumeLines = ["w-11/12", "w-9/12", "w-10/12", "w-7/12"];
  const insightRows = [
    ["Python", "Matched"],
    ["AWS", "Matched"],
    ["Leadership", "Improve"],
  ];

  return (
    <div className="relative w-full max-w-xl">
      <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-blue-200/55 via-cyan-100/45 to-slate-100/40 blur-3xl" />

      <div className="relative overflow-hidden rounded-[2rem] border border-blue-100/80 bg-white shadow-2xl shadow-blue-200/35">
        <div className="border-b border-blue-100 bg-gradient-to-r from-blue-700 via-blue-600 to-cyan-500 p-5 text-white">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-blue-100">
                JobZam AI Workspace
              </p>
              <h2 className="mt-2 text-2xl font-bold text-white">
                Resume match report ready
              </h2>
              <p className="mt-2 text-sm font-medium text-blue-50">
                Analysis complete - Updated just now
              </p>
            </div>

            <div className="rounded-2xl bg-white px-4 py-3 text-center text-blue-700 shadow-lg">
              <p className="text-2xl font-black leading-none">{matchScore}%</p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-wide">
                Match
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 p-5 md:grid-cols-[1fr_0.9fr]">
          <div className="rounded-3xl border border-gray-100 bg-gray-50 p-4">
            <div className="mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              <p className="font-semibold text-slate-950">Resume uploaded</p>
            </div>

            <div className="space-y-2">
              {resumeLines.map((widthClass, index) => (
                <div
                  key={widthClass}
                  className={`hero-resume-line h-3 ${widthClass} rounded-full ${
                    index === 0 ? "bg-slate-300" : "bg-slate-200"
                  }`}
                  style={{ animationDelay: `${index * 140}ms` }}
                />
              ))}
            </div>

            <div className="mt-5 rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">
                AI generated
              </p>
              <p className="mt-2 text-sm font-medium text-gray-700">
                Add measurable impact, role keywords, and stronger action verbs.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-3xl border border-blue-100 bg-blue-50 p-4">
              <div className="mb-3 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <p className="font-semibold text-slate-950">ATS insights</p>
              </div>

              <div className="space-y-3">
                {insightRows.map(([label, status], index) => (
                  <div
                    key={label}
                    className="hero-insight-row flex items-center justify-between rounded-2xl bg-white px-3 py-2 text-sm"
                    style={{ animationDelay: `${700 + index * 180}ms` }}
                  >
                    <span className="font-medium text-gray-700">{label}</span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                        status === "Matched"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-slate-950 p-4 text-white">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-200">
                Next step
              </p>
              <p className="mt-2 font-semibold">
                Generate tailored resume + cover letter
              </p>
              <a
                href="/upload"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-gray-950"
              >
                Start now <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hero-resume-line,
        .hero-insight-row {
          opacity: 0;
          transform: translateY(8px);
          animation: heroPreviewIn 0.5s ease-out forwards;
        }

        @keyframes heroPreviewIn {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-resume-line,
          .hero-insight-row {
            opacity: 1;
            transform: none;
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, href, cta }) {
  return (
    <div className="group rounded-[1.5rem] border border-blue-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-100/70">
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
        <Icon className="h-6 w-6" />
      </div>

      <h3 className="text-xl font-bold text-slate-900">{title}</h3>

      <p className="mt-3 leading-7 text-gray-600">{description}</p>

      <a
        href={href}
        className="mt-5 inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700"
      >
        {cta} <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen overflow-x-clip bg-white px-4 sm:px-6 md:px-16">
      {/* HERO */}
      <section className="relative mx-auto grid max-w-7xl items-center gap-8 bg-white pb-8 pt-20 sm:pb-12 sm:pt-20 md:gap-12 md:py-20 lg:grid-cols-[1fr_0.9fr]">
        <div className="absolute left-[-150px] top-16 h-72 w-72 rounded-full bg-blue-100/45 blur-3xl" />
        <div className="absolute right-[-130px] top-28 h-80 w-80 rounded-full bg-cyan-100/55 blur-3xl" />
        <div className="absolute left-1/4 top-24 h-52 w-52 rounded-full bg-sky-50/70 blur-3xl" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/80 to-transparent" />

        <div className="relative z-10">
          <div className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-blue-100 bg-white px-3 py-1.5 text-xs font-bold text-blue-700 shadow-sm sm:mb-5 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm">
            <Sparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            AI career platform built in London
          </div>

          <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl md:text-6xl lg:text-[4.25rem] lg:leading-[1.02]">
            Land more{" "}
            <span className="bg-gradient-to-r from-blue-700 to-cyan-600 bg-clip-text text-transparent">
              interviews
            </span>{" "}
            with{" "}
            <span className="bg-gradient-to-r from-blue-700 to-indigo-600 bg-clip-text text-transparent">
              AI
            </span>
            .
          </h1>

          <p className="mt-4 max-w-2xl text-base leading-7 text-slate-600 sm:mt-5 sm:text-lg sm:leading-8 md:mt-6 md:text-xl md:leading-9">
            JobZam is an AI resume optimizer that matches your CV with real job
            descriptions, improves your ATS resume score, generates tailored
            resumes and cover letters, and supports interview preparation — all
            in one place.
          </p>

          <div className="mt-5 space-y-2.5 sm:mt-7 sm:space-y-3">
            <div className="flex items-start gap-3 text-sm text-gray-700 md:text-base">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-blue-600" />
              <span>
                Used by over <strong>15,000+ job seekers</strong> worldwide.
              </span>
            </div>

            <div className="flex items-start gap-3 text-sm text-gray-700 md:text-base">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-none text-blue-600" />
              <span>
                Selected for the <GoogleText /> for Startups Cloud Program.
              </span>
            </div>

            <div className="flex items-start gap-3 text-sm text-gray-700 md:text-base">
              <Settings className="mt-0.5 h-5 w-5 flex-none text-blue-600" />
              <span>
                Powered by AI. Built with{" "}
                <span className="text-red-500">love</span> in London.
              </span>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row">
            <a
              href="/upload"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-8 py-4 text-base font-black text-white shadow-xl shadow-blue-200 transition hover:-translate-y-0.5 hover:shadow-2xl"
            >
              Get Started Free <ArrowRight className="h-5 w-5" />
            </a>

            <a
              href="/jobs"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-8 py-4 text-base font-black text-gray-900 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:text-blue-700 hover:shadow-lg"
            >
              Explore Jobs <Briefcase className="h-5 w-5" />
            </a>
          </div>

          <p className="mt-3 text-sm text-gray-500">
            Quick &amp; secure - Google sign-in required to continue.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:mt-10 sm:grid-cols-4">
            {[
              ["15K+", "Job Seekers"],
              ["250+", "Premium Members"],
              ["20+", "Countries"],
              ["4", "AI Career Tools"],
            ].map(([value, label]) => (
              <div
                key={label}
                className="rounded-2xl border border-blue-100 bg-white/85 p-4 shadow-sm shadow-blue-100/40"
              >
                <p className="text-2xl font-black text-blue-700 md:text-3xl">
                  {value}
                </p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500 md:text-sm md:normal-case md:tracking-normal">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 flex justify-center lg:justify-end">
          <HeroProductPreview />
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="mx-auto max-w-7xl pb-8">
        <div className="grid gap-3 rounded-[1.5rem] border border-blue-100 bg-white/80 p-4 shadow-sm backdrop-blur md:grid-cols-4">
          {[
            [ShieldCheck, "Trusted Security"],
            [Brain, "AI Career Platform"],
            [BarChart3, "ATS Score Insights"],
            [Globe2, "Used in 20+ Countries"],
          ].map(([Icon, label]) => (
            <div
              key={label}
              className="flex items-center gap-3 rounded-2xl px-3 py-3 text-gray-700"
            >
              <Icon className="h-5 w-5 text-blue-600" />
              <span className="font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Scroll-generated product demo */}
      <StickyWorkflowShowcase />

      {/* PRODUCT FEATURES */}
      <section className="mx-auto max-w-7xl py-12">
        <div className="mb-10 max-w-3xl">
          <p className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-blue-600">
            Career tools
          </p>

          <h2 className={sectionHeadingClass}>
            Everything you need{" "}
            <span className={gradientTextClass}>to apply smarter.</span>
          </h2>

          <p className="mt-5 text-lg leading-8 text-gray-600">
            JobZam combines resume matching, AI document generation, job search
            tools, and interview preparation into one clean workflow.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          <FeatureCard
            icon={BarChart3}
            title="ATS Resume Optimizer"
            description="Upload your CV, paste a job description, and get a clear ATS match score with missing keywords and skills."
            href="/upload"
            cta="Check ATS score"
          />

          <FeatureCard
            icon={FileText}
            title="AI Cover Letter"
            description="Generate role-specific cover letters based on your CV and the actual job description."
            href="/cover-letter"
            cta="Create cover letter"
          />

          <FeatureCard
            icon={Briefcase}
            title="Job Search"
            description="Explore real jobs, save roles, and use JobZam tools to improve your application before applying."
            href="/jobs"
            cta="Explore jobs"
          />

          <FeatureCard
            icon={MessageSquare}
            title="Interview Prep"
            description="Prepare with role-specific questions, answer guidance, and confidence-building practice."
            href="/interview-prep"
            cta="Practice now"
          />
        </div>
      </section>

      {/* SEO ATS SECTION */}
      <section className="mx-auto max-w-7xl py-8">
        <div className="grid gap-8 rounded-[2rem] border border-blue-100 bg-blue-50/70 p-6 shadow-xl shadow-blue-100/50 md:p-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700">
              <Zap className="h-4 w-4" />
              Built for real job descriptions
            </div>

            <h2 className={sectionHeadingClass}>
              ATS resume optimizer{" "}
              <span className={gradientTextClass}>and job match score.</span>
            </h2>

            <p className="mt-5 text-lg leading-8 text-gray-700">
              An ATS resume checker compares your CV with the role you want to
              apply for. JobZam reads your resume and the job description,
              estimates how closely they match, highlights missing skills and
              keywords, then helps you generate a clearer, role-ready resume.
            </p>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <a
                href="/upload"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-6 py-3 font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5"
              >
                Check my ATS score <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="/interview-prep"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3 font-bold text-gray-900 transition hover:border-blue-200 hover:text-blue-700"
              >
                Try interview prep
              </a>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {[
              ["Resume match score", "See a practical score based on your CV and the job description."],
              ["Missing keywords", "Review the skills, tools, and terms your resume may need to include."],
              ["Optimized resume", "Generate a stronger ATS-friendly version while keeping your experience truthful."],
              ["Cover letter", "Turn the same role context into a tailored cover letter."],
            ].map(([title, description]) => (
              <div
                key={title}
                className="rounded-3xl border border-blue-100 bg-blue-50/60 p-5"
              >
                <Star className="h-5 w-5 text-blue-600" />
                <h3 className="mt-4 text-lg font-bold text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 leading-7 text-gray-600">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT / TRUST */}
      <section className="mx-auto max-w-7xl py-12">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
          <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-sm md:p-8">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-blue-600">
              About JobZam
            </p>

            <h2 className={sectionHeadingClass}>
              A digital-native AI career platform{" "}
              <span className={gradientTextClass}>for modern job seekers.</span>
            </h2>

            <p className="mt-5 text-lg leading-8 text-gray-700">
              JobZamÂ® helps jobseekers improve resume performance in Applicant
              Tracking Systems, compare resumes against job descriptions,
              generate AI-optimised resumes and cover letters, and prepare for
              interviews with role-specific guidance.
            </p>

            <p className="mt-4 text-lg leading-8 text-gray-700">
              JobZamÂ® is live at <strong>jobzam.co</strong> and is used by{" "}
              <strong>15,000+ jobseekers</strong> globally.
            </p>

            <a
              href="/about"
              className="mt-6 inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700"
            >
              Learn more about JobZam <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          <div className="rounded-[2rem] bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-500 p-6 text-white shadow-2xl shadow-blue-200 md:p-8">
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-blue-100">
              Start free
            </p>

            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white md:text-4xl">
              Turn your next job description{" "}
              <span className="bg-gradient-to-r from-white to-cyan-100 bg-clip-text text-transparent">
                into a stronger application.
              </span>
            </h2>

            <p className="mt-5 text-lg leading-8 text-blue-50">
              Upload your CV, check your match score, and generate tailored
              application materials in minutes.
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="/upload"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-black text-blue-700 transition hover:-translate-y-0.5"
              >
                Generate resume <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 font-black text-white transition hover:-translate-y-0.5 hover:bg-white/20"
              >
                View pricing
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
